import { useState } from "react";
import { AlertTriangle, Check, Download, FileCheck2, FileText, Loader2, X } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { EditCard, applyOptimisticResolution, type EditResolution } from "@/components/assistant/EditCard";
import { MessageTimeline } from "@/components/assistant/MessageTimeline";
import type { AssistantChatMessage, DocCreatedEvent, DocEditedEvent, OpenDocumentRequest, ResearchReportEvent } from "@/components/assistant/types";
import { isEditAnnotation } from "@/components/assistant/types";
import { ResearchReport } from "@/components/assistant/ResearchReport";
import { DocCitationIdentity, WebCitationIdentity } from "@/components/identity";
import { invalidatePdfBytes } from "@/hooks/useFetchPdfBytes";
import { invalidateDocxBytes } from "@/hooks/useFetchDocxBytes";
import { api } from "@/lib/api";
import { isWebCitation, type CitationObject, type EditAnnotation, type WebCitationObject } from "@/lib/stream";

/** Request to open the citations inspector tab from an assistant message. */
export interface OpenCitationsRequest {
  citations: Array<CitationObject | WebCitationObject>;
  turnId: string;
}

interface AssistantMessageProps {
  message: AssistantChatMessage;
  onOpenDocument: (request: OpenDocumentRequest) => void;
  onOpenCitations?: (request: OpenCitationsRequest) => void;
  editStatuses?: Map<string, "pending" | "accepted" | "rejected">;
}

function citationRef(annotation: CitationObject | EditAnnotation | WebCitationObject) {
  const ref = "ref" in annotation ? annotation.ref : undefined;
  return ref === undefined || ref === null ? null : String(ref);
}

function isActionableCitation(citation: CitationObject | WebCitationObject): boolean {
  if (isWebCitation(citation)) return Boolean(citation.url?.trim());
  const doc = citation as CitationObject;
  return Boolean(doc.document_id?.trim());
}

function preprocessCitations(text: string, annotations: Array<CitationObject | EditAnnotation | WebCitationObject>) {
  text = text.replace(/<CITATIONS>[\s\S]*?(?:<\/CITATIONS>|$)/g, "").trimEnd();
  const citations: Array<CitationObject | WebCitationObject> = [];
  const processed = text.replace(/\[((?:\d+)(?:\s*,\s*\d+)*)\]/g, (match, group: string) => {
    const tokens = group.split(",").map((item) => item.trim());
    const replacements = tokens.map((token) => {
      const found = annotations.find((annotation) => !isEditAnnotation(annotation) && citationRef(annotation) === token) as CitationObject | WebCitationObject | undefined;
      if (!found || !isActionableCitation(found)) {
        return `[${token}]`;
      }
      const index = citations.push(found) - 1;
      return `\`§${index}§\``;
    });
    return replacements.join(" ");
  });
  return { processed, citations };
}

function MarkdownContent({
  text,
  annotations,
  onOpenDocument
}: {
  text: string;
  annotations: Array<CitationObject | EditAnnotation | WebCitationObject>;
  onOpenDocument: (request: OpenDocumentRequest) => void;
}) {
  const { processed, citations } = preprocessCitations(text, annotations);

  return (
    <div className="prose prose-sm max-w-none text-foreground">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex]}
        components={{
          code({ children, className, ...props }) {
            const value = String(children).trim();
            const match = /^§(\d+)§$/.exec(value);
            if (match) {
              const citation = citations[Number(match[1])];
              if (citation) {
                if (isWebCitation(citation)) {
                  const ref = citationRef(citation);
                  const refNum = ref != null && /^\d+$/.test(ref) ? Number(ref) : undefined;
                  return (
                    <WebCitationIdentity
                      refNumber={refNum}
                      url={citation.url ?? ""}
                      title={citation.title}
                      snippet={citation.snippet}
                      retrievedAt={citation.retrieved_at}
                      size="sm"
                    />
                  );
                }
                const docCitation = citation as CitationObject;
                const ref = citationRef(docCitation);
                const refNum = ref != null && /^\d+$/.test(ref) ? Number(ref) : undefined;
                const verified =
                  docCitation.lexical_ok === true
                    ? "verified"
                    : docCitation.lexical_ok === false || docCitation.verification?.status === "unverified"
                      ? "unverified"
                      : docCitation.verification?.status === "verified"
                        ? "verified"
                        : undefined;
                return (
                  <DocCitationIdentity
                    refNumber={refNum ?? docCitation.ref ?? Number(match[1]) + 1}
                    filename={docCitation.filename}
                    page={docCitation.page != null ? Number(docCitation.page) : undefined}
                    quote={docCitation.quote}
                    verified={verified}
                    reason={docCitation.reason}
                    size="sm"
                    onClick={() =>
                      onOpenDocument({
                        documentId: docCitation.document_id ?? "",
                        filename: docCitation.filename ?? "Document",
                        versionId: docCitation.version_id,
                        versionNumber: docCitation.version_number,
                        kind: "citation",
                        citation: docCitation
                      })
                    }
                  />
                );
              }
            }
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }
        }}
      >
        {processed}
      </ReactMarkdown>
    </div>
  );
}

function DownloadCard({ event, onOpenDocument }: { event: DocCreatedEvent | DocEditedEvent; onOpenDocument: (request: OpenDocumentRequest) => void }) {
  if (!event.document_id) {
    return null;
  }

  return (
    <div className="mt-3 flex items-center gap-3 rounded-md border bg-card p-3">
      <FileCheck2 className="size-5 text-primary" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{event.filename}</p>
        <p className="text-xs text-muted-foreground">{event.version_number ? `Version ${event.version_number}` : "Generated document"}</p>
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() =>
          onOpenDocument({
            documentId: event.document_id!,
            filename: event.filename,
            versionId: event.version_id,
            versionNumber: event.version_number,
            kind: "document"
          })
        }
      >
        Open
      </Button>
      {event.download_url ? (
        <Button type="button" size="sm" asChild>
          <a href={event.download_url} target="_blank" rel="noreferrer">
            <Download className="size-4" />
            Download
          </a>
        </Button>
      ) : null}
    </div>
  );
}

interface EditCardItem {
  annotation: EditAnnotation;
  filename: string;
}

function EditCardsSection({
  items,
  editStatuses,
  onOpenDocument
}: {
  items: EditCardItem[];
  editStatuses?: Map<string, EditResolution>;
  onOpenDocument: (request: OpenDocumentRequest) => void;
}) {
  const [localStatuses, setLocalStatuses] = useState<Map<string, EditResolution>>(() => new Map());
  const [bulkAction, setBulkAction] = useState<"accept" | "reject" | null>(null);
  const [error, setError] = useState<string | null>(null);

  const statusFor = (annotation: EditAnnotation) => localStatuses.get(annotation.edit_id) ?? editStatuses?.get(annotation.edit_id) ?? annotation.status ?? "pending";
  const pendingItems = items.filter(({ annotation }) => statusFor(annotation) === "pending");

  const updateStatus = (annotation: EditAnnotation, status: EditResolution) => {
    setLocalStatuses((current) => {
      const next = new Map(current);
      next.set(annotation.edit_id, status);
      return next;
    });
  };

  const resolveAll = async (action: "accept" | "reject") => {
    if (bulkAction || pendingItems.length === 0) {
      return;
    }

    setBulkAction(action);
    setError(null);
    const nextStatus = action === "accept" ? "accepted" : "rejected";
    const changedDocuments = new Set<string>();

    for (const { annotation } of pendingItems) {
      const revert = applyOptimisticResolution(annotation, action);
      updateStatus(annotation, nextStatus);
      try {
        if (action === "accept") {
          await api.documents.acceptEdit(annotation.document_id, annotation.edit_id);
        } else {
          await api.documents.rejectEdit(annotation.document_id, annotation.edit_id);
        }
        changedDocuments.add(annotation.document_id);
      } catch (resolveError) {
        revert();
        updateStatus(annotation, "pending");
        setError(resolveError instanceof Error ? resolveError.message : "Could not update one or more edits.");
      }
    }

    changedDocuments.forEach((documentId) => {
      invalidateDocxBytes(documentId);
      invalidatePdfBytes(documentId);
    });
    setBulkAction(null);
  };

  return (
    <section className="mt-4 rounded-md border bg-card p-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase text-muted-foreground">{items.length} tracked changes</p>
          <p className="mt-1 text-xs text-muted-foreground">{pendingItems.length} pending</p>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={pendingItems.length === 0 || Boolean(bulkAction)} onClick={() => void resolveAll("reject")}>
          {bulkAction === "reject" ? <Loader2 className="size-4 animate-spin" /> : <X className="size-4" />}
          Reject all
        </Button>
        <Button type="button" size="sm" disabled={pendingItems.length === 0 || Boolean(bulkAction)} onClick={() => void resolveAll("accept")}>
          {bulkAction === "accept" ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
          Accept all
        </Button>
      </div>
      {error ? <p className="mt-2 text-xs text-destructive">{error}</p> : null}
      <details className="mt-3" open>
        <summary className="cursor-pointer text-xs font-medium text-muted-foreground">Review changes</summary>
        <div className="mt-3 space-y-3">
          {items.map(({ annotation, filename }) => (
            <EditCard
              key={annotation.edit_id}
              annotation={annotation}
              filename={filename}
              resolvedStatus={statusFor(annotation)}
              onResolved={(resolvedAnnotation, status) => updateStatus(resolvedAnnotation, status)}
              onOpenEdit={(edit) =>
                onOpenDocument({
                  documentId: edit.document_id,
                  filename,
                  versionId: edit.version_id,
                  versionNumber: edit.version_number,
                  kind: "edit",
                  edit
                })
              }
            />
          ))}
        </div>
      </details>
    </section>
  );
}



export function AssistantMessage({ message, onOpenDocument, onOpenCitations, editStatuses }: AssistantMessageProps) {
  const events = message.events ?? (message.content ? [{ id: `${message.id}-content`, type: "content", text: message.content }] : []);
  const visibleEvents = events.filter((event) => event.type !== "thinking");
  const annotations = message.annotations ?? [];
  const final = !message.isStreaming;
  const contentEvents = visibleEvents.filter((e) => e.type === "content");
  // research_report renders as a full panel below; exclude it from the timeline to avoid a double row.
  const timelineEvents = visibleEvents.filter((e) => e.type !== "content" && e.type !== "research_report");

  const downloads = final
    ? Array.from(
        new Map(
          visibleEvents
            .filter((event): event is DocCreatedEvent | DocEditedEvent => (event.type === "doc_created" || event.type === "doc_edited") && Boolean(event.document_id))
            .map((event) => [event.document_id, event])
        ).values()
      )
    : [];

  const editAnnotations = final
    ? visibleEvents.flatMap((event) => (event.type === "doc_edited" && event.annotations ? event.annotations.map((annotation) => ({ annotation, filename: event.filename })) : []))
    : [];

  const citationAnnotations = final
    ? annotations.filter((annotation): annotation is CitationObject | WebCitationObject => {
        if (isEditAnnotation(annotation)) return false;
        return isActionableCitation(annotation as CitationObject | WebCitationObject);
      })
    : [];

  return (
    <article className="group w-full">
      {contentEvents.map((e) =>
        e.type === "content" ? (
          <MarkdownContent key={e.id} text={e.text} annotations={annotations} onOpenDocument={onOpenDocument} />
        ) : null
      )}

      <MessageTimeline
        events={timelineEvents}
        isStreaming={Boolean(message.isStreaming)}
        hasError={Boolean(message.error)}
        duration_ms={message.duration_ms}
        onOpenDocument={onOpenDocument}
      />

      {visibleEvents
        .filter((e): e is ResearchReportEvent => e.type === "research_report")
        .map((e) => (
          <ResearchReport key={e.id} report={e.report} onOpenDocument={onOpenDocument} />
        ))}

      {downloads.map((event) => (
        <DownloadCard key={`${event.type}-${event.document_id}-${event.version_id}`} event={event} onOpenDocument={onOpenDocument} />
      ))}

      {editAnnotations.length > 1 ? (
        <EditCardsSection items={editAnnotations} editStatuses={editStatuses} onOpenDocument={onOpenDocument} />
      ) : editAnnotations.length ? (
        <div className="mt-4 space-y-3">
          {editAnnotations.map(({ annotation, filename }) => (
            <EditCard
              key={annotation.edit_id}
              annotation={annotation}
              filename={filename}
              resolvedStatus={editStatuses?.get(annotation.edit_id)}
              onOpenEdit={(edit) =>
                onOpenDocument({
                  documentId: edit.document_id,
                  filename,
                  versionId: edit.version_id,
                  versionNumber: edit.version_number,
                  kind: "edit",
                  edit
                })
              }
            />
          ))}
        </div>
      ) : null}

      {final && message.error ? (
        <Alert variant="destructive" icon={<AlertTriangle className="size-4" />} className="mt-3 text-sm">
          {message.error}
        </Alert>
      ) : null}

      {final && citationAnnotations.length > 0 && onOpenCitations ? (
        <button
          type="button"
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
          onClick={() => onOpenCitations({ citations: citationAnnotations, turnId: message.id })}
        >
          <FileText className="size-3" />
          {citationAnnotations.length} citation{citationAnnotations.length === 1 ? "" : "s"}
        </button>
      ) : null}
    </article>
  );
}
